import { Alert, Box, IconButton, Stack, Tooltip } from '@mui/material';
import DeleteIcon from '@mui/icons-material/Delete';
import OpenInNewIcon from '@mui/icons-material/OpenInNew';
import PlayArrowIcon from '@mui/icons-material/PlayArrow';
import ReplayIcon from '@mui/icons-material/Replay';
import StopIcon from '@mui/icons-material/Stop';
import type { GridColDef } from '@mui/x-data-grid';
import { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { resources } from '../../core/services/resources';
import { useReportsFilter } from '../../core/contexts/ReportsFilterContext';
import { extractErrorMessage } from '../../core/services/api';
import { PageHeader } from '../../shared/components/PageHeader';
import { DataTable } from '../../shared/components/DataTable/DataTable';
import { ConfirmDialog } from '../../shared/components/Dialogs/ConfirmDialog';
import { StatusChip } from '../../shared/components/StatusChip';
import { formatDate } from '../../shared/utils/format';
import type { ReportInstance, ReportInstanceStatus } from '../../core/types/data-models';

/**
 * Status transitions surfaced as a row-level icon button. The renderer
 * picks the entry by the row's current `status`; statuses that don't
 * appear here (e.g. DONE) render no action because there's nothing
 * meaningful to flip to from the list view.
 */
const STATUS_ACTIONS: Partial<
  Record<
    ReportInstanceStatus,
    { label: string; icon: typeof PlayArrowIcon; nextStatus: ReportInstanceStatus }
  >
> = {
  PENDING:  { label: 'Run',    icon: PlayArrowIcon, nextStatus: 'ACTIVE' },
  FAILED:   { label: 'Re-run', icon: ReplayIcon,    nextStatus: 'ACTIVE' },
  DISABLED: { label: 'Re-run', icon: ReplayIcon,    nextStatus: 'ACTIVE' },
  ACTIVE:   { label: 'Stop',   icon: StopIcon,      nextStatus: 'DISABLED' },
};

export function ReportInstancesPage() {
  const queryClient = useQueryClient();
  const navigate = useNavigate();
  const [pagination, setPagination] = useState({ page: 0, pageSize: 25 });
  const [error, setError] = useState<string | null>(null);
  const [confirmDelete, setConfirmDelete] = useState<ReportInstance | null>(null);

  // Sidebar filters (Reports Monitoring module). No customer selected →
  // cross-tenant scope: every instance the user has access to.
  const { tenantId, lineOfBizId, reportTemplateId } = useReportsFilter();
  const scope = tenantId ?? ('*' as const);

  // The instances API filters by reportTemplateId only, so a LOB filter is
  // resolved into that LOB's template ids first. Skipped when an explicit
  // template is already selected.
  const lobTemplates = useQuery({
    queryKey: ['report-templates', 'by-lob', scope, lineOfBizId],
    queryFn: () =>
      resources.reportTemplates.list(
        { pageSize: 500, filter: { lineOfBizId: lineOfBizId! } },
        { tenantId: scope },
      ),
    enabled: lineOfBizId != null && reportTemplateId == null,
  });

  // undefined → no template filter; [] → the LOB has no templates, so the
  // list is empty by definition and we skip the fetch.
  const templateFilterIds =
    reportTemplateId != null
      ? [reportTemplateId]
      : lineOfBizId != null
        ? lobTemplates.data?.data.map((t) => t.id)
        : undefined;
  const waitingForLob =
    lineOfBizId != null && reportTemplateId == null && lobTemplates.data === undefined;
  const emptyByFilter = templateFilterIds !== undefined && templateFilterIds.length === 0;

  // Filter changes can invalidate the current page index.
  useEffect(() => {
    setPagination((p) => (p.page === 0 ? p : { ...p, page: 0 }));
  }, [scope, lineOfBizId, reportTemplateId]);

  const list = useQuery({
    queryKey: ['report-instances', pagination, scope, templateFilterIds],
    queryFn: () =>
      resources.reportInstances.list(
        {
          page: pagination.page + 1,
          pageSize: pagination.pageSize,
          sort: '-id',
          filter: templateFilterIds?.length
            ? { reportTemplateId: templateFilterIds }
            : undefined,
        },
        { tenantId: scope },
      ),
    placeholderData: (prev) => prev,
    enabled: !waitingForLob && !emptyByFilter,
  });

  // Template catalog → name lookup, under the same scope as the list so
  // cross-tenant rows resolve too.
  const templates = useQuery({
    queryKey: ['report-templates', 'select', scope],
    queryFn: () =>
      resources.reportTemplates.list({ pageSize: 500, sort: 'name' }, { tenantId: scope }),
  });

  // Tenant catalog → name lookup for the Customer column (tenantless call).
  const tenants = useQuery({
    queryKey: ['tenants', 'select'],
    queryFn: () => resources.tenants.list({ pageSize: 500, sort: 'name' }),
  });
  // Line-of-business catalog → name lookup, under the same scope as the
  // list so cross-tenant rows resolve too.
  const linesOfBusiness = useQuery({
    queryKey: ['lines-of-business', 'select', scope],
    queryFn: () =>
      resources.linesOfBusiness.list({ pageSize: 500, sort: 'name' }, { tenantId: scope }),
  });
  const tenantNameById = useMemo(() => {
    const map = new Map<number, string>();
    for (const t of tenants.data?.data ?? []) map.set(t.id, t.name);
    return map;
  }, [tenants.data]);
  const templateNameById = useMemo(() => {
    const map = new Map<number, string>();
    for (const t of templates.data?.data ?? []) map.set(t.id, t.name);
    return map;
  }, [templates.data]);
  // Instances carry only `reportTemplateId`; the LOB is reached through the
  // template. This maps templateId → lineOfBizId so the column can resolve
  // the LOB name without an extra per-row fetch.
  const templateLobById = useMemo(() => {
    const map = new Map<number, number>();
    for (const t of templates.data?.data ?? []) map.set(t.id, t.lineOfBizId);
    return map;
  }, [templates.data]);
  const lobNameById = useMemo(() => {
    const map = new Map<number, string>();
    for (const l of linesOfBusiness.data?.data ?? []) map.set(l.id, l.name);
    return map;
  }, [linesOfBusiness.data]);

  // PUT just the `status` field — leaves the rest of the row alone. The
  // backend's update schema for ReportInstance accepts partial bodies.
  // Writes can't run in cross-tenant scope, so each mutation carries the
  // row's own tenant.
  const updateStatus = useMutation({
    mutationFn: (args: { id: number; tenantId: number; status: ReportInstanceStatus }) =>
      resources.reportInstances.update(
        args.id,
        { status: args.status } as Partial<ReportInstance>,
        { tenantId: args.tenantId },
      ),
    onSuccess: () => {
      setError(null);
      queryClient.invalidateQueries({ queryKey: ['report-instances'] });
    },
    onError: (err) => setError(extractErrorMessage(err)),
  });

  // Deletes the instance and (server-side) all its generated data —
  // prompts, overviews, insights, recommendations, source visibilities,
  // topics — plus prompt responses no other instance references.
  const remove = useMutation({
    mutationFn: (row: ReportInstance) =>
      resources.reportInstances.remove(row.id, { tenantId: row.tenantId }),
    onSuccess: () => {
      setError(null);
      setConfirmDelete(null);
      queryClient.invalidateQueries({ queryKey: ['report-instances'] });
    },
    onError: (err) => {
      setConfirmDelete(null);
      setError(extractErrorMessage(err));
    },
  });

  const columns: GridColDef<ReportInstance>[] = useMemo(
    () => [
      { field: 'id', headerName: 'ID', width: 80 },
      {
        field: 'tenantId',
        headerName: 'Customer',
        width: 160,
        renderCell: (p) => {
          const id = p.value as number | null | undefined;
          if (id == null) return '';
          return tenantNameById.get(id) ?? `#${id}`;
        },
      },
      {
        field: '__lineOfBiz',
        headerName: 'Line of business',
        width: 200,
        sortable: false,
        filterable: false,
        // Resolved via reportTemplateId → template.lineOfBizId → LOB name;
        // fall back to `#<id>` while the catalogs load.
        renderCell: (p) => {
          const lobId = templateLobById.get((p.row as ReportInstance).reportTemplateId);
          if (lobId == null) return '';
          return lobNameById.get(lobId) ?? `#${lobId}`;
        },
      },
      {
        field: 'reportTemplateId',
        headerName: 'Template',
        width: 240,
        // Resolve numeric id → human-readable template name; fall back to
        // `#<id>` while the catalog is loading or for rows whose template
        // isn't in the current page.
        renderCell: (p) => {
          const id = p.value as number | null | undefined;
          if (id == null) return '';
          return templateNameById.get(id) ?? `#${id}`;
        },
      },
      {
        field: 'promptCount',
        headerName: 'Prompts',
        width: 110,
        type: 'number',
        // Server-computed aggregate (count of ReportInstancePrompt rows);
        // not a real column, so it can't be server-sorted.
        sortable: false,
        align: 'left',
        headerAlign: 'left',
      },
      {
        field: 'status',
        headerName: 'Status',
        width: 130,
        renderCell: (p) => <StatusChip value={p.value as string} />,
      },
      {
        field: 'priority',
        headerName: 'Priority',
        width: 130,
        renderCell: (p) => <StatusChip value={p.value as string} />,
      },
      {
        field: 'createdAt',
        headerName: 'Started',
        width: 200,
        valueFormatter: (v) => formatDate(v as string),
      },
      {
        field: 'updatedAt',
        headerName: 'Updated',
        width: 200,
        valueFormatter: (v) => formatDate(v as string),
      },
      {
        field: '__action',
        headerName: '',
        sortable: false,
        filterable: false,
        // Three IconButtons (open + status action + delete) + cell padding.
        width: 170,
        align: 'right',
        headerAlign: 'right',
        renderCell: (p) => {
          const row = p.row as ReportInstance;
          const action = STATUS_ACTIONS[row.status];
          const pending = updateStatus.isPending && updateStatus.variables?.id === row.id;
          return (
            <Stack
              direction="row"
              spacing={0.5}
              alignItems="center"
              justifyContent="flex-end"
              sx={{ height: '100%' }}
            >
              <Tooltip title="Open details">
                <IconButton
                  size="small"
                  color="primary"
                  onClick={() => navigate(`/report-instances/${row.id}`)}
                  aria-label={`open report instance ${row.id}`}
                >
                  <OpenInNewIcon fontSize="small" />
                </IconButton>
              </Tooltip>
              {/* Status command: Run / Re-run / Stop. Only rendered when a
                  transition is defined for the row's current status (DONE
                  is terminal — no icon). Disabled while this row's
                  mutation is in flight so double-clicks can't double-fire. */}
              {action && (
                <Tooltip title={action.label}>
                  <span>
                    <IconButton
                      size="small"
                      color={action.nextStatus === 'DISABLED' ? 'error' : 'primary'}
                      disabled={pending}
                      onClick={() =>
                        updateStatus.mutate({
                          id: row.id,
                          tenantId: row.tenantId,
                          status: action.nextStatus,
                        })
                      }
                      aria-label={`${action.label.toLowerCase()} report instance ${row.id}`}
                    >
                      <action.icon fontSize="small" />
                    </IconButton>
                  </span>
                </Tooltip>
              )}
              <Tooltip title="Delete">
                <span>
                  <IconButton
                    size="small"
                    color="error"
                    disabled={remove.isPending}
                    onClick={() => setConfirmDelete(row)}
                    aria-label={`delete report instance ${row.id}`}
                  >
                    <DeleteIcon fontSize="small" />
                  </IconButton>
                </span>
              </Tooltip>
            </Stack>
          );
        },
      },
    ],
    [templateNameById, tenantNameById, templateLobById, lobNameById, updateStatus, remove.isPending, navigate],
  );

  return (
    // Flex column so the DataTable can claim the remaining viewport
    // height. Same pattern as `CrudPage` and the detail-page tabs —
    // without `flex: 1, minHeight: 0` the table falls back to its 540 px
    // default instead of filling the available area.
    <Box sx={{ display: 'flex', flexDirection: 'column', flex: 1, minHeight: 0 }}>
      <PageHeader title="Report instances" />
      {error && (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>
          {error}
        </Alert>
      )}
      <DataTable<ReportInstance>
        rows={emptyByFilter ? [] : (list.data?.data ?? [])}
        columns={columns}
        loading={list.isFetching || waitingForLob}
        rowCount={emptyByFilter ? 0 : (list.data?.page.total ?? 0)}
        paginationMode="server"
        paginationModel={pagination}
        onPaginationModelChange={setPagination}
        errorMessage={list.isError ? extractErrorMessage(list.error) : undefined}
        hideFilters
        hideImport
      />
      <ConfirmDialog
        open={!!confirmDelete}
        title="Delete report instance"
        message={`Delete report instance #${confirmDelete?.id} and all its generated data (prompts, insights, recommendations, source visibility)? This cannot be undone.`}
        confirmLabel="Delete"
        destructive
        loading={remove.isPending}
        onConfirm={() => confirmDelete && remove.mutate(confirmDelete)}
        onClose={() => setConfirmDelete(null)}
      />
    </Box>
  );
}
